import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Avatar,
  Grid,
  Tabs,
  Tab,
  Autocomplete,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { productionOrdersApi, ProductionOrder, ProductionOrderStatus } from '../api/productionOrders';
import { designBomApi, DesignBOMItem } from '../api/designBom';
import api from '../services/api';

const STATUS_CONFIG: Record<ProductionOrderStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  DRAFT: { label: 'Draft', color: 'default' },
  READY: { label: 'Ready', color: 'info' },
  CUTTING: { label: 'Cutting', color: 'primary' },
  STITCHING: { label: 'Stitching', color: 'warning' },
  FINISHING: { label: 'Finishing', color: 'secondary' },
  COMPLETED: { label: 'Completed', color: 'success' },
  ON_HOLD: { label: 'On Hold', color: 'default' },
  CANCELLED: { label: 'Cancelled', color: 'error' },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: 'default' | 'error' | 'warning' | 'info' }> = {
  LOW: { label: 'Low', color: 'default' },
  NORMAL: { label: 'Normal', color: 'info' },
  HIGH: { label: 'High', color: 'warning' },
  URGENT: { label: 'Urgent', color: 'error' },
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function ProductionOrdersPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [designs, setDesigns] = useState<any[]>([]);

  // Helper: default target date 15 days from now
  const defaultTargetDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 15);
    return d.toISOString().slice(0, 10);
  };

  // Create form state
  const [newOrder, setNewOrder] = useState({
    design_no: '',
    target_date: defaultTargetDate(),
    notes: '',
    priority: 'NORMAL',
    top_xs: 0, top_s: 0, top_m: 0, top_l: 0, top_xl: 0, top_xxl: 0, top_3xl: 0,
    bottom_xs: 0, bottom_s: 0, bottom_m: 0, bottom_l: 0, bottom_xl: 0, bottom_xxl: 0, bottom_3xl: 0,
    jacket_xs: 0, jacket_s: 0, jacket_m: 0, jacket_l: 0, jacket_xl: 0, jacket_xxl: 0, jacket_3xl: 0,
  });
  const [selectedBomItems, setSelectedBomItems] = useState<DesignBOMItem[]>([]);
  const [selectedAllocations, setSelectedAllocations] = useState<any[]>([]);
  const [maxProducible, setMaxProducible] = useState<{ top: number; bottom: number; jacket: number } | null>(null);

  // SIZE_RATIO: S=1, M=2, L=2, XL=2, XXL=2, 3XL=1  (total units = 10)
  const SIZE_RATIO: Record<string, number> = { S: 1, M: 2, L: 2, XL: 2, XXL: 2, '3XL': 1 };
  const RATIO_TOTAL = Object.values(SIZE_RATIO).reduce((s, v) => s + v, 0);

  const distributeByRatio = (total: number) => {
    const result: Record<string, number> = {};
    let remaining = total;
    const entries = Object.entries(SIZE_RATIO);
    entries.forEach(([size, ratio], idx) => {
      if (idx === entries.length - 1) {
        result[size] = Math.max(0, remaining);
      } else {
        const val = Math.floor(total * ratio / RATIO_TOTAL);
        result[size] = val;
        remaining -= val;
      }
    });
    return result;
  };

  // Fetch BOM + allocations and compute max producible when design is selected
  useEffect(() => {
    if (!newOrder.design_no) {
      setSelectedBomItems([]);
      setSelectedAllocations([]);
      setMaxProducible(null);
      return;
    }
    (async () => {
      let bomItems: DesignBOMItem[] = [];
      let allocs: any[] = [];
      try {
        const bomData = await designBomApi.getByDesign(newOrder.design_no);
        bomItems = bomData.bomItems || [];
      } catch { /* ignore */ }
      try {
        const allocData = await api.get(`/design-materials/design/${newOrder.design_no}`);
        allocs = allocData.data || [];
      } catch { /* ignore */ }

      setSelectedBomItems(bomItems);
      setSelectedAllocations(allocs);

      // Calculate max producible per garment part
      // For each non-optional BOM item, compute: available_for_this_material / consumption_with_wastage
      // The minimum across all required items of that garment part = max producible for that part
      const computeMax = (part: string) => {
        const partBom = bomItems.filter((b) => b.garment_part === part && !b.is_optional);
        if (partBom.length === 0) return 0;
        let minPieces = Infinity;
        for (const bom of partBom) {
          const consumptionPerPiece = bom.consumption_per_piece * (1 + (bom.wastage_pct || 0) / 100);
          if (consumptionPerPiece <= 0) continue;
          // Find matching allocation
          const alloc = allocs.find((a: any) => a.material_id === bom.material_id);
          const available = alloc ? Math.max(0, alloc.allocated_qty - alloc.consumed_qty - alloc.reserved_qty) : 0;
          const pieces = Math.floor(available / consumptionPerPiece);
          minPieces = Math.min(minPieces, pieces);
        }
        return minPieces === Infinity ? 0 : minPieces;
      };

      const topMax = computeMax('TOP');
      const bottomMax = computeMax('BOTTOM');
      const jacketMax = computeMax('JACKET');
      setMaxProducible({ top: topMax, bottom: bottomMax, jacket: jacketMax });

      // Auto-fill size grid with ratio
      const topDist = distributeByRatio(topMax);
      const bottomDist = distributeByRatio(bottomMax);
      const jacketDist = distributeByRatio(jacketMax);
      setNewOrder((prev) => ({
        ...prev,
        top_xs: 0, top_s: topDist['S'] || 0, top_m: topDist['M'] || 0, top_l: topDist['L'] || 0,
        top_xl: topDist['XL'] || 0, top_xxl: topDist['XXL'] || 0, top_3xl: topDist['3XL'] || 0,
        bottom_xs: 0, bottom_s: bottomDist['S'] || 0, bottom_m: bottomDist['M'] || 0, bottom_l: bottomDist['L'] || 0,
        bottom_xl: bottomDist['XL'] || 0, bottom_xxl: bottomDist['XXL'] || 0, bottom_3xl: bottomDist['3XL'] || 0,
        jacket_xs: 0, jacket_s: jacketDist['S'] || 0, jacket_m: jacketDist['M'] || 0, jacket_l: jacketDist['L'] || 0,
        jacket_xl: jacketDist['XL'] || 0, jacket_xxl: jacketDist['XXL'] || 0, jacket_3xl: jacketDist['3XL'] || 0,
      }));
    })();
  }, [newOrder.design_no]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const statusFilter = tabValue === 0 ? 'DRAFT,READY,CUTTING,STITCHING,FINISHING' :
                           tabValue === 1 ? 'COMPLETED' :
                           tabValue === 2 ? 'ON_HOLD,CANCELLED' : undefined;
      const data = await productionOrdersApi.getAll({ status: statusFilter });
      setOrders(data);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to fetch orders', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tabValue]);

  const fetchDesigns = useCallback(async () => {
    try {
      const [response, bomDesignNos] = await Promise.all([
        api.get('/designs'),
        designBomApi.getDesignsWithBom(),
      ]);
      const bomSet = new Set(bomDesignNos);
      setDesigns(
        response.data.filter((d: any) =>
          (d.approval_status === 'APPROVED' || d.approval_status === 'HOLD') && bomSet.has(d.design_no)
        )
      );
    } catch (error) {
      console.error('Failed to fetch designs:', error);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchDesigns(); }, [fetchDesigns]);

  const handleCreate = async () => {
    if (!newOrder.design_no) {
      setSnackbar({ open: true, message: 'Please select a design', severity: 'error' });
      return;
    }

    const planned_qty: Record<string, Record<string, number>> = {};
    const parts = ['top', 'bottom', 'jacket'] as const;
    const partLabels = { top: 'TOP', bottom: 'BOTTOM', jacket: 'JACKET' };

    for (const part of parts) {
      const partSizes: Record<string, number> = {};
      for (const size of sizes) {
        const fieldKey = size === '3XL' ? `${part}_3xl` : `${part}_${size.toLowerCase()}`;
        const val = (newOrder as any)[fieldKey] || 0;
        if (val > 0) partSizes[size] = val;
      }
      if (Object.keys(partSizes).length > 0) planned_qty[partLabels[part]] = partSizes;
    }

    try {
      await productionOrdersApi.create({
        design_no: newOrder.design_no,
        planned_qty: Object.keys(planned_qty).length > 0 ? planned_qty : undefined,
        target_date: newOrder.target_date || undefined,
        notes: newOrder.notes || undefined,
        priority: newOrder.priority,
      });
      setSnackbar({ open: true, message: 'Production order created successfully', severity: 'success' });
      setCreateDialogOpen(false);
      setNewOrder({
        design_no: '', target_date: defaultTargetDate(), notes: '', priority: 'NORMAL',
        top_xs: 0, top_s: 0, top_m: 0, top_l: 0, top_xl: 0, top_xxl: 0, top_3xl: 0,
        bottom_xs: 0, bottom_s: 0, bottom_m: 0, bottom_l: 0, bottom_xl: 0, bottom_xxl: 0, bottom_3xl: 0,
        jacket_xs: 0, jacket_s: 0, jacket_m: 0, jacket_l: 0, jacket_xl: 0, jacket_xxl: 0, jacket_3xl: 0,
      });
      fetchOrders();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to create order', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await productionOrdersApi.delete(id);
      setSnackbar({ open: true, message: 'Order deleted', severity: 'success' });
      fetchOrders();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to delete', severity: 'error' });
    }
  };

  const handleRefreshStatus = async (id: number) => {
    try {
      await productionOrdersApi.refreshStatus(id);
      setSnackbar({ open: true, message: 'Status refreshed', severity: 'success' });
      fetchOrders();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to refresh', severity: 'error' });
    }
  };

  const getProgressPercent = (order: ProductionOrder) => {
    if (order.total_planned === 0) return 0;
    const cut = order.total_cut || 0;
    const stitched = order.total_stitched || 0;
    const finished = order.total_finished || 0;
    // Weighted: cutting 30%, stitching 40%, finishing 30%
    const progress = ((cut / order.total_planned) * 30 +
                      (stitched / order.total_planned) * 40 +
                      (finished / order.total_planned) * 30);
    return Math.min(Math.round(progress), 100);
  };

  const renderOrderRow = (order: ProductionOrder) => {
    const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.DRAFT;
    const priorityCfg = PRIORITY_CONFIG[order.priority] || PRIORITY_CONFIG.NORMAL;
    const progress = getProgressPercent(order);

    return (
      <TableRow key={order.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/production-orders/${order.id}`)}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {order.design?.image_url && (
              <Avatar src={order.design.image_url} variant="rounded" sx={{ width: 40, height: 40 }} />
            )}
            <Box>
              <Typography variant="body2" fontWeight={600}>{order.order_number}</Typography>
              <Typography variant="caption" color="text.secondary">{order.design_no}</Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
        </TableCell>
        <TableCell>
          <Chip label={priorityCfg.label} color={priorityCfg.color} size="small" variant="outlined" />
        </TableCell>
        <TableCell align="center">{order.total_planned}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress variant="determinate" value={progress} sx={{ flexGrow: 1, height: 8, borderRadius: 4 }} />
            <Typography variant="caption">{progress}%</Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="caption">
            {order.target_date ? new Date(order.target_date).toLocaleDateString('en-IN') : '-'}
          </Typography>
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => navigate(`/production-orders/${order.id}`)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh Status">
            <IconButton size="small" onClick={() => handleRefreshStatus(order.id)}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!order.cutting_start_date && !order.total_cut && order.status !== 'COMPLETED' && (
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => handleDelete(order.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Production Orders</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
          New Order
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`Active (${orders.length})`} />
        <Tab label="Completed" />
        <Tab label="On Hold / Cancelled" />
      </Tabs>

      {loading ? (
        <LinearProgress />
      ) : orders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No production orders found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order / Design</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell align="center">Planned Qty</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Target Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map(renderOrderRow)}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Production Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={designs}
                getOptionLabel={(d: any) => `${d.design_no}${d.name ? ' - ' + d.name : ''}`}
                value={designs.find((d: any) => d.design_no === newOrder.design_no) || null}
                onChange={(_, val) => setNewOrder({ ...newOrder, design_no: val?.design_no || '' })}
                renderOption={(props, d: any) => (
                  <li {...props} key={d.design_no}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={d.image_url || undefined}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: 'grey.200', fontSize: 12 }}
                      >
                        {!d.image_url && d.design_no?.slice(0, 2)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{d.design_no}</Typography>
                        {d.name && <Typography variant="caption" color="text.secondary">{d.name}</Typography>}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Design" required />}
                isOptionEqualToValue={(opt: any, val: any) => opt.design_no === val.design_no}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                label="Priority"
                value={newOrder.priority}
                onChange={(e) => setNewOrder({ ...newOrder, priority: e.target.value })}
              >
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="NORMAL">Normal</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="URGENT">Urgent</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Target Date"
                type="date"
                value={newOrder.target_date}
                onChange={(e) => setNewOrder({ ...newOrder, target_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* BOM & Allocation Summary */}
            {newOrder.design_no && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={`BOM: ${selectedBomItems.length} items`}
                    color={selectedBomItems.length > 0 ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${selectedAllocations.length} allocation(s)`}
                    color={selectedAllocations.length > 0 ? 'info' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                  {maxProducible && (maxProducible.top > 0 || maxProducible.bottom > 0 || maxProducible.jacket > 0) && (
                    <>
                      {maxProducible.top > 0 && (
                        <Chip label={`Max Top: ${maxProducible.top}`} color="primary" size="small" />
                      )}
                      {maxProducible.bottom > 0 && (
                        <Chip label={`Max Bottom: ${maxProducible.bottom}`} color="primary" size="small" />
                      )}
                      {maxProducible.jacket > 0 && (
                        <Chip label={`Max Jacket: ${maxProducible.jacket}`} color="primary" size="small" />
                      )}
                    </>
                  )}
                </Box>
                {selectedAllocations.length === 0 && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    No materials allocated. Go to <strong>Inventory</strong> to allocate materials to this design.
                  </Alert>
                )}

                {/* BOM Items Table */}
                {selectedBomItems.length > 0 && (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1, maxHeight: 200 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: 12 }}>Material</TableCell>
                          <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: 12 }}>Part</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 600, fontSize: 12 }}>Cons/pc</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 600, fontSize: 12 }}>Allocated</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 600, fontSize: 12 }}>Available</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 600, fontSize: 12 }}>Max Pcs</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedBomItems.filter((b) => !b.is_optional).map((bom) => {
                          const alloc = selectedAllocations.find((a: any) => a.material_id === bom.material_id);
                          const allocated = alloc ? alloc.allocated_qty : 0;
                          const available = alloc ? Math.max(0, alloc.allocated_qty - alloc.consumed_qty - alloc.reserved_qty) : 0;
                          const consWithWastage = bom.consumption_per_piece * (1 + (bom.wastage_pct || 0) / 100);
                          const maxPcs = consWithWastage > 0 ? Math.floor(available / consWithWastage) : 0;
                          return (
                            <TableRow key={bom.id}>
                              <TableCell sx={{ py: 0.25, fontSize: 12 }}>{bom.material_name}</TableCell>
                              <TableCell sx={{ py: 0.25, fontSize: 12 }}>{bom.garment_part}</TableCell>
                              <TableCell align="right" sx={{ py: 0.25, fontSize: 12 }}>{consWithWastage.toFixed(2)} {bom.uom}</TableCell>
                              <TableCell align="right" sx={{ py: 0.25, fontSize: 12 }}>{allocated.toFixed(1)}</TableCell>
                              <TableCell align="right" sx={{ py: 0.25, fontSize: 12, color: available > 0 ? 'success.main' : 'error.main' }}>{available.toFixed(1)}</TableCell>
                              <TableCell align="right" sx={{ py: 0.25, fontSize: 12, fontWeight: 600 }}>{maxPcs}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Divider sx={{ my: 1 }} />
              </Grid>
            )}

            {/* Size quantities - TOP */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>TOP Quantities</Typography>
            </Grid>
            {sizes.map((size) => {
              const key = `top_${size.toLowerCase().replace('3xl', '3xl')}` as keyof typeof newOrder;
              const fieldKey = size === '3XL' ? 'top_3xl' : `top_${size.toLowerCase()}`;
              return (
                <Grid item xs={6} sm={12 / 7} key={`top-${size}`}>
                  <TextField
                    fullWidth
                    label={size}
                    type="number"
                    size="small"
                    value={(newOrder as any)[fieldKey] || 0}
                    onChange={(e) => setNewOrder({ ...newOrder, [fieldKey]: parseInt(e.target.value) || 0 })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              );
            })}

            {/* Size quantities - BOTTOM */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>BOTTOM Quantities</Typography>
            </Grid>
            {sizes.map((size) => {
              const fieldKey = size === '3XL' ? 'bottom_3xl' : `bottom_${size.toLowerCase()}`;
              return (
                <Grid item xs={6} sm={12 / 7} key={`bottom-${size}`}>
                  <TextField
                    fullWidth
                    label={size}
                    type="number"
                    size="small"
                    value={(newOrder as any)[fieldKey] || 0}
                    onChange={(e) => setNewOrder({ ...newOrder, [fieldKey]: parseInt(e.target.value) || 0 })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              );
            })}

            {/* Size quantities - JACKET */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>JACKET Quantities</Typography>
            </Grid>
            {sizes.map((size) => {
              const fieldKey = size === '3XL' ? 'jacket_3xl' : `jacket_${size.toLowerCase()}`;
              return (
                <Grid item xs={6} sm={12 / 7} key={`jacket-${size}`}>
                  <TextField
                    fullWidth
                    label={size}
                    type="number"
                    size="small"
                    value={(newOrder as any)[fieldKey] || 0}
                    onChange={(e) => setNewOrder({ ...newOrder, [fieldKey]: parseInt(e.target.value) || 0 })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              );
            })}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={newOrder.notes}
                onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create Order</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
